import { BdEquals, BdNumberFormatter } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdRangeCondition,
} from '@bindoc-projects/condition-domain-lib';

export const AYS_CLINIC_BEDS_ID = 'clinicBeds';
export const FilterConditionClinicBedsPluginType: FilterConditionPluginSpecification<
  { from: number | null; to: number | null },
  FilterConditionClinicBedsDto,
  AysClinicBedsCondition
> = {
  type: 'clinicBeds',
  category: 'clinic',
  displayName: { de: 'Klinik-Betten' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionClinicBedsDto,
  ): FilterConditionPluginImpl<{ from: number | null; to: number | null }> {
    return new FilterConditionClinicBedsPlugin(obj.from, obj.to);
  },
  fromCondition(jsonObj: AysClinicBedsCondition): FilterConditionPluginImpl<{
      from: number | null;
      to: number | null;
    }> {
    return new FilterConditionClinicBedsPlugin(jsonObj.from, jsonObj.to);
  },
  newInstance(): FilterConditionPluginImpl<{ from: number | null; to: number | null }> {
    return new FilterConditionClinicBedsPlugin(null, null);
  },
};

export class FilterConditionClinicBedsPlugin extends FilterConditionPluginImpl<
  { from: number | null; to: number | null },
  FilterConditionClinicBedsDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    { from: number | null; to: number | null },
    FilterConditionClinicBedsDto,
    AysClinicBedsCondition
  > = FilterConditionClinicBedsPluginType;

  constructor(
    private from: number | null,
    private to: number | null,
  ) {
    super(FilterConditionClinicBedsPluginType.type);
  }

  clone(): FilterConditionClinicBedsPlugin {
    return new FilterConditionClinicBedsPlugin(this.from, this.to);
  }

  override equals(other: FilterConditionClinicBedsPlugin): boolean {
    return (
      super.equals(other) &&
      BdEquals.equals(this.from, other.from) &&
      BdEquals.equals(this.to, other.to)
    );
  }

  override valid(): boolean {
    if (Number.isInteger(this.to) && Number.isInteger(this.from)) {
      return this.from < this.to;
    } else {
      return Number.isInteger(this.to) || Number.isInteger(this.from);
    }
  }

  override validationToText(): string {
    if (Number.isInteger(this.to) && Number.isInteger(this.from)) {
      return this.from < this.to ? '' : 'Min < Max';
    } else if (Number.isInteger(this.to) || Number.isInteger(this.from)) {
      return '';
    } else {
      return 'Betten Min oder Max auswählen';
    }
  }

  toCondition(): BdCondition | null {
    return new AysClinicBedsCondition(this.from, this.to);
  }

  toJSON(): FilterConditionClinicBedsDto | null {
    return {
      type: this.type,
      from: this.from,
      to: this.to,
    };
  }

  toText(): string | null {
    if (Number.isInteger(this.to) && Number.isInteger(this.from)) {
      return `Kliniken zwischen ${BdNumberFormatter.format(this.from)} und ${BdNumberFormatter.format(this.to)} Betten`;
    } else if (Number.isInteger(this.to)) {
      return `Kliniken bis ${BdNumberFormatter.format(this.to)} Betten`;
    } else if (Number.isInteger(this.from)) {
      return `Kliniken ab ${BdNumberFormatter.format(this.from)} Betten`;
    } else {
      return null;
    }
  }

  toValue(): { from: number | null; to: number | null } {
    return {
      from: this.from,
      to: this.to,
    };
  }
}

export class AysClinicBedsCondition extends BdRangeCondition<number> {
  constructor(from: number | null, to: number | null) {
    super(AYS_CLINIC_BEDS_ID, from, to);
  }
}

interface FilterConditionClinicBedsDto {
  type: string;
  from: number | null;
  to: number | null;
}
